import React, { createRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useAuth from '@hooks/use-auth'

import { Button } from '@thriveglobal/thrive-ui-lib'
import InputText from '@elements/InputText'
import {
  LoginContent,
  LoginTitle,
  RequestDemoLink,
  InvalidEmailInformation,
} from '@features/LoginModal/styles'
import { COLORS } from '@enums/colors'

export const LoginModalSignup: React.FC<{
  email: string
  transition: () => void
  onClose: () => void
}> = ({ email, transition, onClose }) => {
  const [formError, setFormError] = useState(null)
  const { signup, isLoading } = useAuth()
  const { t } = useTranslation()

  const firstNameInputRef = createRef<HTMLInputElement>()
  const lastNameInputRef = createRef<HTMLInputElement>()
  const passwordInputRef = createRef<HTMLInputElement>()
  const groupCodeInputRef = createRef<HTMLInputElement>()

  const onSubmit = async () => {
    const firstName = firstNameInputRef.current?.value
    const lastName = lastNameInputRef.current?.value
    const password = passwordInputRef.current?.value
    const companyHint = groupCodeInputRef.current?.value

    if (!firstName || !lastName || !password || !companyHint) return
    try {
      await signup({
        email,
        firstName,
        lastName,
        password,
        companyHint,
      })
      onClose()
    } catch (e) {
      setFormError(e.message)
    }
  }

  return (
    <LoginContent>
      <LoginTitle>{t('sign_up_page_1_title')}</LoginTitle>
      <InputText
        ref={firstNameInputRef}
        placeholder={t('form_fill_first_name')}
        onSubmit={onSubmit}
        isValid={false}
        data-testid="modal-input"
      />
      <InputText
        ref={lastNameInputRef}
        placeholder={t('form_fill_last_name_copy')}
        onSubmit={onSubmit}
        isValid={false}
        data-testid="modal-input"
      />
      <InputText
        ref={passwordInputRef}
        type="password"
        placeholder={t('form_fill_password')}
        onSubmit={onSubmit}
        isValid={false}
        data-testid="modal-input"
      />
      <InputText
        ref={groupCodeInputRef}
        placeholder={t('form_fill_group_code')}
        onSubmit={onSubmit}
        isValid={false}
        data-testid="modal-input"
      />
      <Button loading={isLoading} onClick={onSubmit} data-testid="modal-submit">
        {t('login_modal_button')}
      </Button>
      {formError && (
        <InvalidEmailInformation>{formError}</InvalidEmailInformation>
      )}
      <RequestDemoLink
        data-testid="back-to-login"
        onClick={transition}
        color={COLORS.teal}
      >
        {t('login_modal_back_to_login')}
      </RequestDemoLink>
    </LoginContent>
  )
}
